import { AxiosError } from 'axios';
import { PURGE } from 'redux-persist';

import { createAsyncThunk } from '@reduxjs/toolkit';

import { forgotPassword } from '~modules/auth/api/api-service/forgot-password';
import { resetPassword } from '~modules/auth/api/api-service/reset-password';
import { signIn } from '~modules/auth/api/api-service/sign-in';
import { signOut } from '~modules/auth/api/api-service/sign-out';
import { quickSignup, signUp } from '~modules/auth/api/api-service/sign-up';
import { setAccessToken } from '~modules/auth/store/auth-actions';

import { setTenantAccessToken } from '~tenant-modules/auth/store/tenant-auth-actions';

import { resetAuthSensitiveQueries } from '~system/query/auth-query-cache';
import { setScope } from '~system/store/scope/scope-slice';
import { triggerToast } from '~system/store/toast/toast-slice';

import {
  ApiForgotPasswordRequest,
  ApiLoginRequest,
  ApiResetPasswordRequest,
  ApiSignUpRequest,
} from '~shared/types';

export const signUpThunk = createAsyncThunk(
  'auth/signUp',
  async (signupData: ApiSignUpRequest, thunkAPI) => {
    try {
      const data = await signUp(signupData);
      return data;
    } catch (error) {
      thunkAPI.dispatch(
        triggerToast({
          title: 'Помилка реєстрації',
          message: (error as AxiosError<{ message: string }>).response?.data
            ?.message,
          type: 'warning',
        }),
      );

      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const quickSignUpThunk = createAsyncThunk(
  'auth/quickSignUp',
  async (signupData: ApiSignUpRequest, thunkAPI) => {
    try {
      const data = await quickSignup(signupData);

      resetAuthSensitiveQueries();

      thunkAPI.dispatch(setAccessToken(data.access_token));
      thunkAPI.dispatch(setTenantAccessToken(null));
      thunkAPI.dispatch(setScope('lessor'));

      return data;
    } catch (error) {
      thunkAPI.dispatch(
        triggerToast({
          title: 'Помилка реєстрації',
          message: (error as AxiosError<{ message: string }>).response?.data
            ?.message,
          type: 'warning',
        }),
      );

      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const signInThunk = createAsyncThunk(
  'auth/signIn',
  async (signInData: ApiLoginRequest, thunkAPI) => {
    try {
      const data = await signIn(signInData);

      resetAuthSensitiveQueries();

      thunkAPI.dispatch(setTenantAccessToken(null));
      thunkAPI.dispatch(setScope('lessor'));
      thunkAPI.dispatch(setAccessToken(data.access_token));

      thunkAPI.dispatch(
        triggerToast({
          title: 'Авторизовано',
          message: 'Ви успішно увійшли в систему',
          type: 'success',
        }),
      );

      return data;
    } catch (error) {
      thunkAPI.dispatch(
        triggerToast({
          title: 'Помилка авторизації',
          message: (error as AxiosError<{ message: string }>).response?.data
            ?.message,
          type: 'error',
        }),
      );

      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const signOutThunk = createAsyncThunk(
  'auth/signOut',
  async (_, thunkAPI) => {
    try {
      await signOut();
    } finally {
      resetAuthSensitiveQueries();

      thunkAPI.dispatch(setAccessToken(null));
      thunkAPI.dispatch(setTenantAccessToken(null));
      thunkAPI.dispatch({ type: PURGE, key: 'auth' });
    }
  },
);

export const forceLogoutThunk = createAsyncThunk(
  'auth/forceLogout',
  async (_, thunkAPI) => {
    resetAuthSensitiveQueries();

    thunkAPI.dispatch(setAccessToken(null));
    thunkAPI.dispatch(setTenantAccessToken(null));
    thunkAPI.dispatch({ type: PURGE, key: 'auth' });

    // setTimeout(() => {
    //   thunkAPI.dispatch({ type: PURGE, key: 'auth' });
    // }, 0);

    thunkAPI.dispatch(
      triggerToast({
        title: 'Потрібен повторний вхід',
        message: 'Час дії сесії вичерпано. Будь ласка, увійдіть ще раз.',
        type: 'warning',
      }),
    );

    return true;
  },
);

export const forgotPasswordThunk = createAsyncThunk(
  'auth/forgotPassword',
  async (forgotPasswordData: ApiForgotPasswordRequest, thunkAPI) => {
    try {
      const data = await forgotPassword(forgotPasswordData);

      thunkAPI.dispatch(
        triggerToast({
          title: 'Лист відправлено',
          message: 'На пошту відправлено лист',
          type: 'success',
        }),
      );

      return data;
    } catch (error) {
      thunkAPI.dispatch(
        triggerToast({
          title: 'Помилка відправки листа',
          message: (error as AxiosError<{ message: string }>).response?.data
            ?.message,
          type: 'error',
        }),
      );

      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const resetPasswordThunk = createAsyncThunk(
  'auth/resetPassword',
  async (resetPasswordData: ApiResetPasswordRequest, thunkAPI) => {
    try {
      const data = await resetPassword(resetPasswordData);

      thunkAPI.dispatch(
        triggerToast({
          title: 'Пароль змінено',
          message: 'Ви змінли пароль для акаунту',
          type: 'success',
        }),
      );

      return data;
    } catch (error) {
      thunkAPI.dispatch(
        triggerToast({
          title: 'Помилка зміни паролю',
          message: (error as AxiosError<{ message: string }>).response?.data
            ?.message,
          type: 'error',
        }),
      );

      return thunkAPI.rejectWithValue(error);
    }
  },
);
